import { useQuery } from '@apollo/client';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { decode } from 'he';
import * as R from 'ramda';
import React from 'react';
import { Link } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { OutcropStudiesPageQuery } from '~/apollo/generated/v4/graphql';
import { GeologyTypeIconV4 } from '~/components/common/icons/geology-type-icon-v4';
import { NoItemsAlert } from '~/components/common/NoItemsAlert';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { Well } from '~/components/common/Well';
import {
  ControlledCollapse,
  useCollapseControl,
} from '~/components/ui/collapse';
import { useRouteParam } from '~/hooks/routing';
import { studyRoute } from '~/paths';
import { studyGeologyTypes } from '~/utils/modules/study';
import { stripHtml, truncate, ucwords } from '~/utils/text';

const OUTCROP_STUDIES_PAGE = graphql(`
  query OutcropStudiesPage($id: ID!) {
    outcropGet(id: $id) {
      id
      studies {
        id
        name
        abstract
        collectedYear
        collectedBy
        publicationType
        dominantGeologyType
        keyParameters {
          id
          geologyType
        }
      }
    }
  }
`);

export default function OutcropStudiesRoute() {
  return (
    <ApolloProviderV4>
      <OutcropStudiesRouteInner />
    </ApolloProviderV4>
  );
}

function OutcropStudiesRouteInner() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_STUDIES_PAGE, {
    variables: { id: outcropId },
  });

  const outcrop = data?.outcropGet;
  const studies = R.sortWith(
    [R.descend(study => study.collectedYear), R.ascend(study => study.name)],
    outcrop?.studies ?? [],
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <div className="space-y-4">
      {studies.map(study => (
        <StudyListItem key={study.id} study={study} />
      ))}
      <NoItemsAlert show={studies.length === 0} entity="studies" />
    </div>
  );
}

type Outcrop = NonNullable<OutcropStudiesPageQuery['outcropGet']>;
type Study = NonNullable<Outcrop['studies']>[number];

function StudyListItem({ study }: { study: Study }) {
  const abstractControl = useCollapseControl(false);

  const ReadMoreButton = (
    <Link to={studyRoute(parseInt(study.id))} className="link">
      ...read more
    </Link>
  );

  const abstract: React.ReactNode = R.pipe(
    () => study,
    study => study.abstract ?? '',
    stripHtml,
    decode,
    truncate(300, true, ReadMoreButton),
  )();

  const publicationType = study.publicationType
    ?.filter(p => p !== 'unknown' && p !== 'undefined')
    .map(ucwords)
    .join(', ');

  const geologyTypes = studyGeologyTypes(study);

  return (
    <div className="card shadow">
      <div className="card-body">
        <div className="grid lg:grid-cols-12 gap-x-2 gap-y-1">
          <div className="lg:col-span-5">
            <Link
              to={studyRoute(parseInt(study.id))}
              className="link font-bold"
            >
              {geologyTypes.map(geologyType => (
                <GeologyTypeIconV4
                  key={geologyType}
                  geologyType={geologyType}
                  className="w-6 mr-1 -mb-1.5"
                />
              ))}
              <span>{study.name}</span>
            </Link>
          </div>
          <div className="lg:col-span-1">{study.collectedYear}</div>
          <div className="lg:col-span-2">{study.collectedBy}</div>
          <div className="lg:col-span-2">{publicationType}</div>
          <div className="lg:col-span-2 text-right">
            <Link
              to={studyRoute(parseInt(study.id))}
              className="btn btn-sm btn-primary gap-1"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} /> View
            </Link>
          </div>
        </div>

        {study.abstract && (
          <Well>
            <ControlledCollapse
              {...abstractControl}
              title="Abstract"
              titleClassName="font-bold"
            >
              {abstract}
            </ControlledCollapse>
          </Well>
        )}
      </div>
    </div>
  );
}
